// GRID ↔ LIST view switcher (TypeScript, webpack-friendly)
// - SSR: w products.tpl ustaw klasę wg {$smarty.cookies.ps_product_view}
// - TS: zapisuje cookie i przełącza klasy bez użycia parametru w URL
// - Odporne na psFacetedSearchUpdated / updateProductList

type Root = Document | HTMLElement;

const COOKIE = 'ps_product_view';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 rok

function setCookie(name: string, value: string, maxAgeSeconds = COOKIE_MAX_AGE) {
  document.cookie = `${name}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
}

function getCookie(name: string): string | undefined {
  const m = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return m ? decodeURIComponent(m[1]) : undefined;
}

type Mode = 'grid' | 'list';

function detectInitialMode(listEl: HTMLElement): Mode {
  const saved = getCookie(COOKIE);
  if (saved === 'grid' || saved === 'list') return saved;
  return listEl.classList.contains('product-list--list') ? 'list' : 'grid';
}

function applyModeToList(listEl: HTMLElement, mode: Mode) {
  listEl.classList.toggle('product-list--list', mode === 'list');
  listEl.classList.toggle('product-list--grid', mode === 'grid');
}

function updateButtonsState(btnGrid: HTMLElement | null, btnList: HTMLElement | null, mode: Mode) {
  btnGrid?.setAttribute('aria-pressed', String(mode === 'grid'));
  btnList?.setAttribute('aria-pressed', String(mode === 'list'));
}

function findControls(context: Root) {
  const btnGrid = context.querySelector<HTMLElement>('.js-view-grid');
  const btnList = context.querySelector<HTMLElement>('.js-view-list');
  return { btnGrid, btnList };
}

function findLists(context: Root): HTMLElement[] {
  const lists = Array.from(context.querySelectorAll<HTMLElement>('.js-product-list, #js-product-list'));
  return Array.from(new Set(lists));
}

/** Prosty „bind once” po data-atrybucie, żeby eventy się nie dublowały */
function bindOnce(el: Element, key: string, binder: () => void) {
  const attr = `data-vs-bound-${key}`;
  if (el instanceof HTMLElement && el.hasAttribute(attr)) return;
  binder();
  if (el instanceof HTMLElement) el.setAttribute(attr, '1');
}

/** Public: inicjalizacja w aktualnym kontekście DOM */
export function initViewSwitcher(root: Root = document): void {
  const lists = findLists(root);
  if (!lists.length) return;

  const isViewPage = document.body?.id === 'view'; // <--- NOWE


  // Ustal tryb z cookie/SSR i zastosuj
  // const mode = detectInitialMode(lists[0]);
  const mode: Mode = isViewPage ? 'list' : detectInitialMode(lists[0]);
  lists.forEach((list) => applyModeToList(list, mode));

  const { btnGrid, btnList } = findControls(root);
  updateButtonsState(btnGrid, btnList, mode);

  // Handlery kliknięć (po 1 razie)
  if (!isViewPage && btnGrid) {
    bindOnce(btnGrid, 'grid', () => {
      btnGrid.addEventListener('click', () => {
        const listsNow = findLists(document);
        listsNow.forEach((list) => applyModeToList(list, 'grid'));
        updateButtonsState(btnGrid, btnList ?? null, 'grid');
        setCookie(COOKIE, 'grid');
      });
    });
  }

  if (btnList) {
    bindOnce(btnList, 'list', () => {
      btnList.addEventListener('click', () => {
        const listsNow = findLists(document);
        listsNow.forEach((list) => applyModeToList(list, 'list'));
        updateButtonsState(btnGrid ?? null, btnList, 'list');
        setCookie(COOKIE, 'list');
      });
    });
  }
}

/** Public: podpinanie eventów PS (faceted search/AJAX) + fallback MutationObserver */
export function bindViewSwitcherEvents(prestashop?: any): void {
  // ps_facetedsearch emituje to przy odświeżeniu listy
  document.addEventListener('psFacetedSearchUpdated', (e: Event) => {
    const ctx: Root = (e as any).detail?.productList || document;
    initViewSwitcher(ctx);
  });

  // PrestaShop event bus (jeżeli obecny)
  if (prestashop && typeof prestashop.on === 'function') {
    prestashop.on('updateProductList', (data: any) => {
      const ctx: Root = (data && (data.html as Root)) || document;
      initViewSwitcher(ctx);
    });
    prestashop.on('updatedProduct', () => initViewSwitcher());
    prestashop.on('updatedCart', () => initViewSwitcher());
  }

  // Fallback — gdy inny moduł podmieni DOM bez eventu
  const target = document.getElementById('js-product-list') || document.body;
  const mo = new MutationObserver(() => initViewSwitcher());
  mo.observe(target, { childList: true, subtree: true });
}

/** Optional default export dla wygody */
export default {
  initViewSwitcher,
  bindViewSwitcherEvents,
};
